import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import BannerComp from "../components/BannerComp";
import BlogLoadingState from "../components/BlogLoadingState";
import RelatedPost from "../components/RelatedPost";
import Seo from "../components/Seo";
import NotFound from "../NotFound";
import api, { apiError } from "../lib/api";
import RenderToast from "../appcomponents/RenderToast";
import { createBlogAutoSchema, normalizeSchemaMarkup } from "../utils/blogSchema";
import { buildCanonicalUrl } from "../lib/seo";
import { sanitizeBlogHtml } from "../utils/blogHtml";
import styles from "./BlogDetail.module.css";

function RichText({ html, className = "" }) {
  if (!html) {
    return null;
  }

  return (
    <div
      className={`${styles.richText} ${className}`.trim()}
      dangerouslySetInnerHTML={{ __html: sanitizeBlogHtml(html) }}
    />
  );
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await api.get("/api/public/blogs.php", { params: { slug } });
        const payload = data.data || {};
        if (!active) return;
        setBlog(payload);

        const listRes = await api.get("/api/public/blogs.php");
        if (active) setAllPosts(listRes.data?.data?.items || []);
      } catch (err) {
        if (active) {
          RenderToast({ type: "error", message: apiError(err) });
          setBlog(null);
        }
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [slug]);

  const relatedPosts = useMemo(
    () => allPosts.filter((post) => post.slug !== blog?.slug).slice(0, 4),
    [allPosts, blog?.slug]
  );

  if (!loading && !blog) {
    return <NotFound />;
  }

  if (loading) {
    return <BlogLoadingState fullScreen title="Loading article" message="Preparing the blog content for you." />;
  }

  if (!blog) {
    return <div />;
  }

  const canonicalPath = `/blog/${blog.slug}`;
  const canonicalUrl = blog.canonical_url || buildCanonicalUrl(canonicalPath);
  const customSchema = normalizeSchemaMarkup(blog.schema_markup);
  const schema = customSchema
    ? customSchema
    : createBlogAutoSchema({
        title: blog.title,
        excerpt: blog.excerpt,
        metaDescription: blog.meta_description,
        image: blog.image || blog.image_url,
        publishedAt: blog.published_at,
        slug: blog.slug,
        canonicalUrl,
      });
  const resolvedMetaTitle = blog.meta_title || blog.title;

  return (
    <>
      <Seo
        title={`${resolvedMetaTitle} | PSP RCM Blog`}
        metaTitle={`${resolvedMetaTitle} | PSP RCM Blog`}
        description={blog.meta_description}
        canonicalPath={canonicalPath}
        canonicalUrl={canonicalUrl}
        schema={schema}
        image={blog.image}
        type="article"
      />
      <BannerComp
        heading={blog.title}
        paragraph={blog.excerpt}
        BannerBgImg="/pages-banner.webp"
        centered
      />

      <section className={styles.section}>
        <div className={`container ${styles.layout}`}>
          <article className={styles.article}>
            {blog.image ? (
              <img src={blog.image} alt={blog.title} className={styles.coverImage} />
            ) : (
              <div className={styles.coverFallback}>{blog.title}</div>
            )}

            <div className={styles.body}>
              {blog.content ? (
                <RichText html={blog.content} />
              ) : (
                blog.sections?.map((section) => (
                  <section
                    key={section.heading ?? section.paragraphs?.[0] ?? blog.slug}
                    className={styles.contentBlock}
                  >
                    {section.heading ? <h2>{section.heading}</h2> : null}
                    {section.paragraphs?.map((paragraph) => (
                      <RichText key={paragraph} html={paragraph} />
                    ))}
                  </section>
                ))
              )}
            </div>
          </article>

          <div className={styles.sidebar}>
            <RelatedPost posts={relatedPosts} />
          </div>
        </div>
      </section>
    </>
  );
}
